import {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  startTransition,
} from "react";
// ===== REFACTORED: Editor is now in EditorSection component =====
import {
  Box,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import QuestionContent from "../QuestionContent";
import CommonModal from "../CommonModal/CommonModal";
import TextAnswerRetryStatus from "../TextAnswerRetryStatus";
import {
  runCode as runCodeApi,
  runCodeWithCustomInput as runCodeWithCustomInputApi,
  saveDraft as saveDraftApi,
  loadDraft as loadDraftApi,
  saveEditorEvents as saveEditorEventsApi,
} from "../../services/assessment.api";
import { useDispatch, useSelector } from "react-redux";
import {
  setAnswer,
  setValue,
  setQuestionIndex,
  recordQuestionCheating,
} from "../../store/Test/action";
import { serverURL } from "../../utils/programming/apiCall";
import TypingAnalyzer from "../../utils/programming/typingAnalyzer";
// ===== REFACTORED: Import extracted utilities and hooks =====
import { getMonacoLanguage } from "../../utils/programming/monacoLanguageMapping";
import {
  formatCode,
  formatSelection,
} from "../../utils/programming/monacoEditorUtils";
import {
  clearDiagnostics,
  applyDiagnostics,
} from "../../utils/programming/errorDiagnostics";
import { useEditorEventTracking } from "../../hooks/useEditorEventTracking";
import { useCodeAutoSave } from "../../hooks/useCodeAutoSave";
import { useCodeExecution } from "../../hooks/useCodeExecution";
import { useFloatingWindow } from "../../hooks/useFloatingWindow";
import { useAutoSubmission } from "../../hooks/useAutoSubmission";
import QuestionPanel from "./QuestionPanel";
import EditorSection from "./EditorSection";
import MobileProgrammingLayout from "./MobileProgrammingLayout";
import FloatingOutputWindow from "./FloatingOutputWindow";
import ProgrammingPostSubmissionView from "./ProgrammingPostSubmissionView";
import PanelResizer from "./PanelResizer";
import SubmittedProgrammingView from "./SubmittedProgrammingView";

// ===== REFACTORED: All helper functions have been extracted to separate modules =====
// Language mapping: src/utils/monacoLanguageMapping.js
// Code formatters: src/utils/codeFormatters/
// Error diagnostics: src/utils/errorDiagnostics.js
// Monaco editor utils: src/utils/monacoEditorUtils.js
// registerLanguageFormatters is now called in EditorSection component

const ProgrammingTypeQuestion = forwardRef(
  (
    {
      data,
      selectedAnswerData,
      timeRemaining,
      setCandidateAnswer,
      handleNext,
      handleUpdate, // New prop for updating result without navigation
      practiceMode,
      onModalStateChange,
      onAttemptsStatusChange, // New prop
      questionIndex,
    },
    ref,
  ) => {
    const theme = useTheme();

    const dispatch = useDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedLanguage, setSelectedLanguage] = useState(
      data?.supportedLanguages?.[0]?.languageId || null,
    );
    const [showOutput, setShowOutput] = useState(false);
    const [outputHeight, setOutputHeight] = useState(200);
    const [isDragging, setIsDragging] = useState(false);
    const [editorTheme, setEditorTheme] = useState("vs-dark");

    // Tab management
    const [activeTab, setActiveTab] = useState(0);
    const [customInput, setCustomInput] = useState("");
    const [vtLoading, setVtLoading] = useState(false);

    // Loading States
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ===== PHASE 1: ADDED STATE VARIABLES FROM JUDGE0EDITOR.JSX =====

    // Attempt Management
    const [draftLoaded, setDraftLoaded] = useState(false);

    // Initial values from Redux (stable initialization)
    const initialTimeSpent = selectedAnswerData?.timeSpent || 0;
    const maxAttempts = data?.maxAttempts ?? 3;
    const attemptsUsed = selectedAnswerData?.retakes || 0;
    const remaining = Math.max(0, maxAttempts - attemptsUsed);
    const attemptsExhausted = attemptsUsed >= maxAttempts;

    // Question-specific Timer State
    const [timeSpentOnQuestion, setTimeSpentOnQuestion] =
      useState(initialTimeSpent);

    const initialIsAnswered = selectedAnswerData?.isAnswered || false;

    // Notify parent about attempts exhausted status
    useEffect(() => {
      if (onAttemptsStatusChange) {
        // Only true if exhausted AND not answered
        const isExhaustedAndNotSubmitted =
          attemptsExhausted &&
          !(initialIsAnswered || selectedAnswerData?.isAnswered);
        onAttemptsStatusChange(isExhaustedAndNotSubmitted);
      }
    }, [
      attemptsExhausted,
      initialIsAnswered,
      selectedAnswerData?.isAnswered,
      onAttemptsStatusChange,
    ]);
    const [isPostSubmission, setIsPostSubmission] = useState(false);

    // Initial check for submitted state
    useEffect(() => {
      if (initialIsAnswered) {
        setIsPostSubmission(true);
      } else {
        setIsPostSubmission(false);
      }
    }, [initialIsAnswered]);

    // Combine isAnswered and attemptsExhausted for a master lock
    const isQuestionLocked = initialIsAnswered || attemptsUsed >= maxAttempts;
    const [isReadOnly, setIsReadOnly] = useState(isQuestionLocked);

    // Sync local state if Redux changes (ONLY for isAnswered/locked state sync)
    useEffect(() => {
      const locked =
        selectedAnswerData?.isAnswered || attemptsUsed >= maxAttempts;
      if (locked !== isReadOnly) {
        setIsReadOnly(locked);
      }
    }, [selectedAnswerData?.isAnswered, attemptsUsed, maxAttempts]);

    // Combine all lock conditions for hooks and UI
    const isHookDisabled =
      isReadOnly ||
      initialIsAnswered ||
      selectedAnswerData?.isAnswered ||
      isPostSubmission;

    // Use a secondary lock specifically for the editor interaction if needed,
    // but for the PostSubmission view we handle read-only state inside the component.

    // Code state - using ref to prevent unnecessary re-renders
    const [code, setCode] = useState("");
    const codeRef = useRef(""); // Ref to track code without causing re-renders
    const [warningShown, setWarningShown] = useState(false);

    // Sync ref with state to keep it updated
    useEffect(() => {
      codeRef.current = code;
    }, [code]);

    // Get IDs from Redux
    // candidateAssessmentId is the invite ID (from candidateReducer.id)
    // assessmentId is the test/assessment ID (from testReducer.testId)
    const candidateAssessmentId = useSelector(
      (state) => state.candidateReducer.id,
    );
    const assessmentId = useSelector((state) => state.testReducer.testId);
    const questionId = data?._id || "";
    const skillName = data?.skillName || "";
    const testData = useSelector((state) => state.testReducer.data);
    const currentQuestionIndex = useSelector(
      (state) => state.testReducer.questionIndex,
    );
    const value = useSelector((state) => state.testReducer.value);

    // Navigation Handlers for Post-Submission View
    const handleNextQuestion = useCallback(() => {
      const currentSkill = testData[parseInt(value) - 2];
      const difficulties = ["easy", "medium", "hard"];
      const questionTypes = [
        "mcqQuestions",
        "programmingQuestions",
        "sqlQuestions",
      ];

      let allSkillQuestions = [];
      questionTypes.forEach((type) => {
        if (currentSkill[type]) {
          difficulties.forEach((diff) => {
            if (currentSkill[type][`${diff}Questions`]) {
              allSkillQuestions.push(...currentSkill[type][`${diff}Questions`]);
            }
          });
        }
      });

      if (currentQuestionIndex < allSkillQuestions.length - 1) {
        dispatch(setQuestionIndex(currentQuestionIndex + 1));
      } else if (parseInt(value) < testData.length + 1) {
        dispatch(setValue((parseInt(value) + 1).toString()));
        dispatch(setQuestionIndex(0));
      } else {
        // Last question, go to overview
        dispatch(setValue("1"));
      }
    }, [dispatch, testData, value, currentQuestionIndex]);

    const handleBackToOverview = useCallback(() => {
      dispatch(setValue("1"));
    }, [dispatch]);

    const questionTitle = data?.questionTitle || "";
    const difficulty = data?.difficulty || "Easy";
    const sampleInput = data?.sampleInput || "";
    const sampleOutput = data?.sampleOutput || "";
    const testCases = data?.testCases || [];
    const supportedLanguages = data?.supportedLanguages || []; // Removed useMemo as it's just a ref to data
    const timeLimit = data?.timeLimit || 90;
    const memoryLimit = data?.memoryLimit || 128;
    const maxCodeSize = data?.maxCodeSize || 64;
    const weightage = data?.weightage || 1;
    // ✅ REMOVED: maxTime - Programming questions now use total assessment duration only

    // Monaco language mapping - using extracted utility (needed before hooks)
    const monacoLanguage = useMemo(() => {
      return getMonacoLanguage(selectedLanguage);
    }, [selectedLanguage]);

    // Monaco Editor Refs (needed before hooks)
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const componentContainerRef = useRef(null);
    // Ref for direct DOM update of timer to prevent stuck UI
    const timerTextRef = useRef(null);

    // Modal System - Enhanced with proper types and loading states (needed before hooks)
    const [modal, setModal] = useState({
      isOpen: false,
      title: "",
      message: "",
      type: "info", // info, loading, success, error, warning
      isLoading: false,
      showFooter: true,
      icon: null,
    });

    // Enhanced modal system functions (needed before hooks)
    const showModal = useCallback(
      (title, message, type = "info", options = {}) => {
        setModal({
          isOpen: true,
          title,
          message,
          type,
          isLoading: type === "loading" || options.isLoading || false,
          showFooter: options.showFooter !== false,
          icon: options.icon || null,
        });
      },
      [],
    );

    const closeModal = useCallback(() => {
      setModal({
        isOpen: false,
        title: "",
        message: "",
        type: "info",
        isLoading: false,
        showFooter: true,
        icon: null,
      });
    }, []);

    // ===== REFACTORED: Use extracted hooks =====
    // Editor Event Tracking Hook
    const {
      eventBufferRef,
      trackEditorEvent,
      trackInitialState,
      resetEventTracking,
      flushEvents,
      lastChangeEventTimeRef,
      lastCursorEventTimeRef,
      lastSelectionEventTimeRef,
      lastFocusEventTimeRef,
      CHANGE_THROTTLE_MS,
      CURSOR_THROTTLE_MS,
      SELECTION_THROTTLE_MS,
      FOCUS_THROTTLE_MS,
    } = useEditorEventTracking({
      questionId,
      selectedLanguage,
      candidateAssessmentId,
      assessmentId,
      skillName,
      supportedLanguages: data?.supportedLanguages || [],
      practiceMode,
      questionTitle: data?.questionTitle || null,
      questionType: data?.type || "programming",
      isReadOnly: isHookDisabled,
    });

    // Auto-save Hook
    const { saveState, lastSavedAt, autoSaveCode } = useCodeAutoSave({
      code, // Use state to trigger effects
      data,
      selectedLanguage,
      candidateAssessmentId,
      assessmentId,
      skillName,
      questionId,
      practiceMode,
      draftLoaded,
      isReadOnly: isHookDisabled,
    });

    // Floating Window Hook
    const {
      isOutputFloating,
      setIsOutputFloating,
      floatingPosition,
      floatingSize,
      handleFloatingToggle,
      handleWindowDragStart,
      handleResizeStart,
    } = useFloatingWindow();

    const handleUpdateRetakes = useCallback(
      (newCount) => {
        dispatch((dispatchState, getState) => {
          const state = getState();
          const currentAnswer =
            state.testReducer.selectedAnswers[questionId] || {};

          dispatch(
            setAnswer(
              questionId,
              currentAnswer.selectedAnswer || null,
              currentAnswer.isAnswered || false,
              currentAnswer.isReviewed || false,
              timeSpentOnQuestion,
              newCount,
              selectedLanguage,
            ),
          );
        });
      },
      [dispatch, questionId, timeSpentOnQuestion, selectedLanguage],
    );

    // Code Execution Hook - use ref to prevent re-renders on every code change
    const {
      isRunningCode,
      results,
      setResults,
      summary,
      setSummary,
      customResult,
      setCustomResult,
      customLoading,
      setCustomLoading,
      showCelebration,
      setShowCelebration,
      handleRunCode,
      runWithCustomInput: runWithCustomInputHook,
    } = useCodeExecution({
      code: codeRef.current, // Use ref to prevent hook re-initialization
      questionId,
      selectedLanguage,
      attemptsExhausted,
      maxAttempts,
      attemptsUsed,
      candidateAssessmentId,
      assessmentId,
      skillName,
      practiceMode,
      onUpdateRetakes: handleUpdateRetakes,
      editorRef,
      monacoRef,
      monacoLanguage,
      setShowOutput,
      setActiveTab,
    });

    // Wrapper for custom input that supports external code (like sandbox)
    const runWithCustomInput = useCallback(
      async (input, sandboxCode) => {
        // If sandboxCode is provided (from results view), we use it.
        // Otherwise the hook will use codeRef.current (which it's bound to).
        if (sandboxCode) {
          // Special case: Manual API call if we want to run sandbox code
          // because the useCodeExecution hook is bound to codeRef.current
          setCustomLoading(true);
          try {
            const payload = {
              candidateAssessmentId,
              assessmentId,
              questionId,
              skill: skillName || "Programming",
              code: sandboxCode,
              languageId: selectedLanguage,
              customInput: input,
            };
            const response = await runCodeWithCustomInputApi(
              assessmentId,
              payload,
            );
            setCustomResult(response || null);
          } catch (error) {
            console.error("Error running sandbox code:", error);
            setCustomResult({
              success: false,
              error: error.message || "Execution failed",
            });
          } finally {
            setCustomLoading(false);
          }
        } else {
          // Standard case: Use the hook's implementation
          runWithCustomInputHook(input);
        }
      },
      [
        runWithCustomInputHook,
        candidateAssessmentId,
        assessmentId,
        questionId,
        skillName,
        selectedLanguage,
        setCustomLoading,
        setCustomResult,
      ],
    );

    // Final sync ref to avoid stale closures in effects/unmount
    const syncStateRef = useRef({
      questionId,
      selectedAnswer: selectedAnswerData?.selectedAnswer,
      isAnswered: selectedAnswerData?.isAnswered,
      isReviewed: selectedAnswerData?.isReviewed,
      timeSpent: timeSpentOnQuestion,
      retakes: attemptsUsed,
    });

    useEffect(() => {
      syncStateRef.current = {
        questionId,
        selectedAnswer: selectedAnswerData?.selectedAnswer,
        isAnswered: selectedAnswerData?.isAnswered,
        isReviewed: selectedAnswerData?.isReviewed,
        timeSpent: timeSpentOnQuestion,
        retakes: attemptsUsed,
        language: selectedLanguage,
      };
    }, [
      questionId,
      selectedAnswerData,
      timeSpentOnQuestion,
      attemptsUsed,
      selectedLanguage,
    ]);

    // Final Sync on Unmount to prevent data loss or state inconsistency
    useEffect(() => {
      return () => {
        const {
          questionId,
          isAnswered: propIsAnswered,
          isReviewed,
          timeSpent,
          retakes,
          language,
        } = syncStateRef.current;

        // Use the absolute latest code from codeRef
        const latestCode = codeRef.current;

        if (questionId) {
          // IMPORTANT: Capture the current Redux state to avoid overwriting a "true" answered status
          // with a stale "false" status during unmount (the prop might be stale)
          dispatch((dispatchState, getState) => {
            const state = getState();
            const currentReduxAnswer =
              state.testReducer.selectedAnswers[questionId];

            // Only update if it's not a status downgrade (don't set Answered=false if it's true in Redux)
            const finalIsAnswered =
              currentReduxAnswer?.isAnswered || propIsAnswered || false;

            dispatch(
              setAnswer(
                questionId,
                latestCode || null,
                finalIsAnswered,
                isReviewed || false,
                timeSpent,
                retakes,
                language,
              ),
            );
          });
        }
      };
    }, [dispatch]); // Only on unmount

    // Auto-submission Hook
    const {
      hasAutoSubmitted,
      setHasAutoSubmitted,
      isSubmitting: isAutoSubmitting,
      setIsSubmitting: setIsAutoSubmitting,
      isSubmittingRef,
    } = useAutoSubmission({
      data,
      selectedLanguage,
      timeRemaining,
      code,
      handleNext,
      autoSaveCode,
      showModal,
      closeModal,
      skillName,
      eventBufferRef,
      isReadOnly: isHookDisabled,
    });

    // Use isSubmitting from auto-submission hook
    const isSubmittingState = isSubmitting || isAutoSubmitting;

    // ✅ Initialize typing analyzer for programming questions (same as SubjectiveTypeQuestion)
    const typingAnalyzerRef = useRef(null);
    if (!typingAnalyzerRef.current) {
      typingAnalyzerRef.current = new TypingAnalyzer({
        enableVerboseLogging: false,
        analysisInterval: 1000,
        ENABLE_GLOBAL_COPY_DETECTION: true,
        ENABLE_QUESTION_COPY_DETECTION: true,
      });
    }
    const typingAnalyzer = typingAnalyzerRef.current;

    // ===== REFACTORED: Monaco Editor Refs are now defined earlier (before hooks) =====

    // ===== PHASE 1: COMPUTED VALUES AND DERIVED STATE =====
    // Note: maxAttempts, attemptsUsed, monacoLanguage, refs, and modal functions are defined earlier (before hooks)

    // Update selectedLanguage when data is loaded
    useEffect(() => {
      if (data?.supportedLanguages?.[0]?.languageId && !selectedLanguage) {
        setSelectedLanguage(data.supportedLanguages?.[0]?.languageId);
      }
    }, [data?.supportedLanguages, selectedLanguage]);

    // Resize functionality
    const LS_KEY = "programming-question-left-width";
    const [leftWidth, setLeftWidth] = useState(() => {
      const saved = Number(localStorage.getItem(LS_KEY));
      return Number.isFinite(saved) && saved > 0 ? saved : 520; // default ~40% on 1440px
    });

    // ===== REFACTORED: Floating window state is now handled by useFloatingWindow hook =====
    const draggingRef = useRef(false);

    // Define question content and memoized setCode before any early returns
    const question = data?.question || "";

    // Memoize setCode callback to prevent re-renders
    const memoizedSetCode = useCallback((newCode) => {
      codeRef.current = newCode;
      setCode(newCode);
    }, []);

    const questionContent = useMemo(() => {
      return (
        <QuestionContent
          question={question}
          className="problem-statement sun-editor-editable"
          style={{
            height: "100%",
          }}
        />
      );
    }, [question]);

    // Memoize complex style objects to prevent re-renders
    const containerStyles = useMemo(
      () => ({
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        height: {
          base: "100%",
          md: "calc(100vh - 350px)",
        },
        width: "100%",
        overflow: "hidden",
        position: "relative",
        gap: isMobile ? 0 : 0,
      }),
      [isMobile],
    );

    const questionContainerStyles = useMemo(
      () => ({
        width: isMobile ? "100%" : `${leftWidth}px`,
        minWidth: isMobile ? "280px" : "320px",
        bgcolor: "#fff",
        borderRadius: "4px",
        height: "100%",
        overflowY: "auto",
        position: "relative",
        flexShrink: 0,
      }),
      [isMobile, leftWidth],
    );

    // ✅ REMOVED: Question-specific time calculations
    // Programming questions now use only the total assessment duration
    // No more per-question timers, warnings, or robot overlays

    // Submit function - now calls handleNext from ScreeningTest
    const handleSubmit = useCallback(async () => {
      // Use ref to get latest code value without causing re-renders
      const currentCode = codeRef.current || code;
      if (!currentCode || !data || !selectedLanguage) return;

      try {
        setIsSubmitting(true);
        isSubmittingRef.current = true;

        showModal(
          "Submitting Code",
          "Please wait while we save your solution...",
          "loading",
          {
            showFooter: false, // Hide footer during loading
            icon: "/assets/loading.svg",
          },
        );

        // Auto-save final code - use current code from ref
        await autoSaveCode();

        // Call handleNext from ScreeningTest with the required parameters
        // This will handle the submission through ScreeningTest's submission flow
        // Process editor events for cheating detection
        const rawEvents = eventBufferRef.current || [];
        const tabSwitchCount = rawEvents.filter(
          (e) => e.type === "tab_switch",
        ).length;
        const fullScreenExitCount = rawEvents.filter(
          (e) => e.type === "full_screen_exit",
        ).length;

        // The editor event buffer contains interaction telemetry (change, focus,
        // blur, cursor, etc.), not clipboard-risk decisions. Use TypingAnalyzer's
        // risk-aware output so routine editor activity and same-editor copy/paste
        // are not reported as cheating.
        const detectedCheatings = typingAnalyzer.getSuspiciousEvents();
        const isCheatingDetected = detectedCheatings.length > 0;

        await handleNext({
          code: currentCode, // Renamed from sourceCode to code to match backend Schema
          programmingLanguageId: selectedLanguage,
          candidateAnswer: currentCode,
          type: "programming",
          skill: skillName,
          timeSpent: timeSpentOnQuestion,
          retakes: attemptsUsed, // Pass retakes to handleNext
          isCheatingDetected,
          detectedCheatings,
          tabSwitchCount,
          fullScreenExitCount,
          editorEvents: rawEvents,
        });

        setIsPostSubmission(true);
        setIsSubmitting(false);
        if (isSubmittingRef) isSubmittingRef.current = false;
        closeModal();
      } catch (error) {
        console.error("Final submission failed:", error);
        // ✅ CRITICAL: Immediately exit loading state on error
        setIsSubmitting(false);
        if (isSubmittingRef) isSubmittingRef.current = false;

        showModal(
          "Submission Failed",
          error.response?.data?.message ||
            error.message ||
            "An unexpected error occurred. Please try again.",
          "error",
          {
            icon: "/assets/error.svg",
            showFooter: true,
          },
        );
      }
      // ✅ REMOVED: finally block - errors are handled explicitly, success is handled by useEffect
    }, [
      code, // Keep code in deps for initial check, but use ref for actual value
      data,
      selectedLanguage,
      timeRemaining,
      showModal,
      closeModal,
      autoSaveCode,
      handleNext,
      skillName,
      timeSpentOnQuestion,
      isSubmittingRef,
    ]);

    // ===== REFACTORED: Error diagnostics are now imported from utils/errorDiagnostics.js =====

    // Language change handler
    const handleLanguageChange = useCallback(
      (newLanguageId) => {
        // Track language change event
        trackEditorEvent(
          "languageChange",
          {
            fromLanguage: selectedLanguage,
            toLanguage: newLanguageId,
            fromLanguageName: supportedLanguages.find(
              (l) => l.languageId === selectedLanguage,
            )?.languageName,
            toLanguageName: supportedLanguages.find(
              (l) => l.languageId === newLanguageId,
            )?.languageName,
          },
          0,
          lastFocusEventTimeRef,
        );

        // Clear results on language change to avoid confusion
        setResults([]);
        setCustomResult(null);
        setSummary(null);

        // Clear celebration animation from previous language
        setShowCelebration(false);

        // Clear any error diagnostics from previous language
        if (editorRef.current && monacoRef.current) {
          clearDiagnostics(editorRef.current, monacoRef.current);
        }

        // Reset output panel to input tab when language changes
        setActiveTab(0);
      },
      [
        selectedLanguage,
        supportedLanguages,
        trackEditorEvent,
        lastFocusEventTimeRef,
        setResults,
        setCustomResult,
        setSummary,
        setShowCelebration,
        setActiveTab,
      ],
    );

    // Format code handler
    const handleFormatCode = useCallback(async () => {
      if (editorRef.current && monacoRef.current) {
        const success = await formatCode(editorRef.current, monacoRef.current);
        if (success) {
          console.log("Code formatted successfully");
        }
      }
    }, []);

    // Clamp and persist when leftWidth changes - optimized with debouncing
    useEffect(() => {
      const min = 300;
      const max = Math.max(480, window.innerWidth * 0.7);
      const clamped = Math.min(Math.max(leftWidth, min), max);
      if (clamped !== leftWidth) setLeftWidth(clamped);

      // Debounce localStorage writes to reduce performance impact
      const timeoutId = setTimeout(() => {
        localStorage.setItem(LS_KEY, String(clamped));
      }, 100);

      return () => clearTimeout(timeoutId);
    }, [leftWidth]);

    // ✅ NEW: Reset submission state when question ID changes
    useEffect(() => {
      // Reset local state when question ID changes
      setIsSubmitting(false);
      if (isSubmittingRef) isSubmittingRef.current = false;
      closeModal(); // Close any open modals
    }, [data?._id, closeModal, isSubmittingRef]);

    // Load draft or Starter Code when question/language changes
    useEffect(() => {
      const loadDraftOrStarterCode = async () => {
        if (!questionId || !selectedLanguage) return;

        // Reset event tracking for new question/language
        resetEventTracking();

        // Reset warning shown flag for new question
        setWarningShown(false);

        let finalCode = "";

        // ✅ PRACTICE MODE FIX: Skip loading draft in practice mode
        if (practiceMode) {
          // In practice mode, always use starter code
          const starterCode = supportedLanguages.find(
            (language) => language.languageId === selectedLanguage,
          )?.starterCode;
          finalCode = starterCode || "";
        } else {
          // Try draft first in production mode
          try {
            // Need to pass assessmentId (which is testId) and query params
            const response = await loadDraftApi(assessmentId, {
              candidateAssessmentId: candidateAssessmentId,
              questionId: questionId,
              languageId: selectedLanguage,
            });
            const draftData = response;

            if (draftData?.draft?.code != null) {
              finalCode = draftData.draft.code;
            } else {
              const starterCode = supportedLanguages.find(
                (language) => language.languageId === selectedLanguage,
              )?.starterCode;
              finalCode = starterCode || "";
            }
          } catch {
            // Fallback to starter code on any error
            try {
              const starterCode = supportedLanguages.find(
                (language) => language.languageId === selectedLanguage,
              )?.starterCode;
              finalCode = starterCode || "";
            } catch {
              finalCode = "";
            }
          }
        }

        // ✅ NEW: Track initial state BEFORE setting code
        trackInitialState(finalCode, selectedLanguage);

        // Set the code (this will trigger change events, but we now have initial state)
        codeRef.current = finalCode;
        setCode(finalCode);

        // Mark draft as loaded to allow auto-save
        setDraftLoaded(true);
      };

      setDraftLoaded(false);
      loadDraftOrStarterCode();
    }, [
      questionId,
      selectedLanguage,
      practiceMode,
      candidateAssessmentId,
      assessmentId,
      supportedLanguages,
      resetEventTracking,
      trackInitialState,
    ]);

    // Reset local states ONLY when question ID truly changes
    useEffect(() => {
      if (questionId) {
        // No more setRetakes(0) here, we rely on individual question retakes in Redux
        setTimeSpentOnQuestion(selectedAnswerData?.timeSpent || 0);
        setWarningShown(false);
        // ✅ REMOVED: setHasShownTenPercentWarning - no longer needed
      }
    }, [questionId]); // removed dispatch to keep it focused

    // Refs to avoid timer restarts on dependency changes
    const isReadOnlyRef = useRef(isReadOnly);
    const practiceModeRef = useRef(practiceMode);
    useEffect(() => {
      isReadOnlyRef.current = isReadOnly || isPostSubmission;
      practiceModeRef.current = practiceMode;
    }, [isReadOnly, practiceMode, isPostSubmission]);

    // Sync isReadOnly with submission status (removed time-based lock)
    useEffect(() => {
      const shouldBeReadOnly = !!(
        selectedAnswerData?.isAnswered || isPostSubmission
      );

      if (shouldBeReadOnly !== isReadOnly) {
        setIsReadOnly(shouldBeReadOnly);
      }
    }, [selectedAnswerData?.isAnswered, isReadOnly, isPostSubmission]);

    // ✅ REMOVED: Question-specific timer logic
    // Programming questions now track time spent but don't enforce per-question limits
    // Timer continues to track time for analytics purposes only
    useEffect(() => {
      const timerId = setInterval(() => {
        if (!isReadOnlyRef.current && !practiceModeRef.current) {
          setTimeSpentOnQuestion((prev) => prev + 1);
        }
      }, 1000);

      return () => clearInterval(timerId);
    }, [questionId]);

    const timeSpentRef = useRef(timeSpentOnQuestion);
    useEffect(() => {
      timeSpentRef.current = timeSpentOnQuestion;
    }, [timeSpentOnQuestion]);

    // ✅ REMOVED: Auto-submit and time warning logic
    // Programming questions no longer have per-question time limits
    // Only the overall assessment timer applies

    // ===== REFACTORED: Event flushing is now handled by useEditorEventTracking hook =====

    // ✅ NEW: Tab visibility handling to ensure timer works correctly when switching tabs
    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.log("📱 Tab became hidden - timer continues in background");
        } else {
          console.log("📱 Tab became visible - checking timer state");
          // When tab becomes visible, ensure auto-submission logic is still working
          // The timer should continue running in the background, but we need to ensure
          // the auto-submission logic is still active
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    }, []);

    // ===== REFACTORED: Auto-submission is now handled by useAutoSubmission hook =====

    // Global mouse handlers for drag (exact same as reference App.js)
    useEffect(() => {
      const onMove = (e) => {
        if (!draggingRef.current) return;
        const minLeft = 280;
        const minRight = 420;
        const maxLeft = Math.max(minLeft, window.innerWidth - minRight);
        const next = Math.min(Math.max(e.clientX, minLeft), maxLeft);
        setLeftWidth(next);
      };
      const onUp = () => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        setIsDragging(false);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      return () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
    }, []);

    // Panel resizer handler
    const startDrag = (e) => {
      e.preventDefault();
      draggingRef.current = true;
      setIsDragging(true);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    };

    // ===== REFACTORED: Floating window handlers are now in useFloatingWindow hook =====

    // ===== REFACTORED: Code execution is now handled by useCodeExecution hook =====
    // ===== REFACTORED: Tab change is handled by OutputPanel component =====

    // ===== PHASE 9: REQUIRED METHODS FOR SCREENINGTEST.JSX INTEGRATION =====

    // Method to get current answer (required by ScreeningTest.jsx)
    const getCurrentAnswer = useCallback(() => {
      return code;
    }, [code]);

    // Method to get typing analysis (same as SubjectiveTypeQuestion)
    const getTypingAnalysis = useCallback(() => {
      return typingAnalyzer.getAnalysis();
    }, [typingAnalyzer]);

    // Method to get copy-paste analysis (required by ScreeningTest.jsx)
    const getCopyPasteAnalysis = useCallback(() => {
      // Return mock copy-paste analysis data for backward compatibility
      return {
        isSuspicious: false,
        hasQuestionCopying: false,
        questionCopyCount: 0,
        hasOptionCopying: false,
        optionCopyCount: 0,
        hasFullQuestionCopying: false,
        fullQuestionCopyCount: 0,
        analysis: "No suspicious copy-paste activity detected",
      };
    }, []);

    // Method to get programming language ID (required by ScreeningTest.jsx)
    const getProgrammingLanguageId = useCallback(() => {
      return selectedLanguage;
    }, [selectedLanguage]);

    // Method to cleanup resources (required by ScreeningTest.jsx)
    const cleanup = useCallback(() => {
      // Note: auto-save and flush timers are handled internally by hooks

      // Close any open modals
      closeModal();

      // Cleanup typing analyzer (same as SubjectiveTypeQuestion)
      typingAnalyzer.destroy();

      // Flush any remaining events using hook's flush function
      if (eventBufferRef.current.length > 0) {
        flushEvents();
      }
    }, [closeModal, typingAnalyzer, eventBufferRef, flushEvents]);

    // Expose methods to parent component via ref
    useImperativeHandle(
      ref,
      () => ({
        getCurrentAnswer,
        getTypingAnalysis,
        getCopyPasteAnalysis,
        getProgrammingLanguageId,
        cleanup,
        handleSubmit,
        // ✅ OPTIMIZED: Expose editor events directly from buffer
        getEditorEvents: () => eventBufferRef.current,
      }),
      [
        getCurrentAnswer,
        getTypingAnalysis,
        getCopyPasteAnalysis,
        getProgrammingLanguageId,
        cleanup,
        handleSubmit,
      ],
    );

    // ===== REFACTORED: SavedBadge is now in EditorSection component =====

    // Early return if data is not loaded yet
    if (
      !data ||
      !data.supportedLanguages ||
      data.supportedLanguages.length === 0
    ) {
      return (
        <Box sx={containerStyles}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: "#00C49A" }} />
            <Typography sx={{ color: "#666", fontSize: "14px" }}>
              Loading programming question...
            </Typography>
          </Box>
        </Box>
      );
    }

    // Render mobile layout if on mobile device
    if (isMobile) {
      if (selectedAnswerData?.isAnswered) {
        return (
          <Box ref={componentContainerRef} sx={containerStyles}>
            <SubmittedProgrammingView
              code={selectedAnswerData.selectedAnswer || code}
              language={getMonacoLanguage(
                selectedAnswerData.language || selectedLanguage,
              )}
              timeSpent={selectedAnswerData.timeSpent || timeSpentOnQuestion}
              retakes={selectedAnswerData.retakes || attemptsUsed}
              maxAttempts={maxAttempts}
              languageName={
                supportedLanguages.find(
                  (l) =>
                    l.languageId ===
                    (selectedAnswerData.language || selectedLanguage),
                )?.languageName || selectedLanguage
              }
            />
          </Box>
        );
      }

      return (
        <Box ref={componentContainerRef} sx={containerStyles}>
          <MobileProgrammingLayout
            // Question Panel Props
            questionTitle={questionTitle}
            questionContent={questionContent}
            sampleInput={sampleInput}
            sampleOutput={sampleOutput}
            timeLimit={timeLimit}
            memoryLimit={memoryLimit}
            maxCodeSize={maxCodeSize}
            supportedLanguages={supportedLanguages}
            // Editor Props
            code={code}
            setCode={memoizedSetCode}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            editorTheme={editorTheme}
            setEditorTheme={setEditorTheme}
            isRunningCode={isRunningCode}
            isSubmitting={isSubmittingState}
            attemptsExhausted={attemptsExhausted}
            attemptsUsed={attemptsUsed}
            maxAttempts={maxAttempts}
            onRunCode={handleRunCode}
            onSubmit={handleSubmit}
            onFormatCode={handleFormatCode}
            saveState={saveState}
            lastSavedAt={lastSavedAt}
            timeRemaining={timeRemaining}
            isReadOnly={isReadOnly}
            showOutput={showOutput}
            setShowOutput={setShowOutput}
            isOutputFloating={false}
            editorRef={editorRef}
            monacoRef={monacoRef}
            trackEditorEvent={trackEditorEvent}
            lastChangeEventTimeRef={lastChangeEventTimeRef}
            lastCursorEventTimeRef={lastCursorEventTimeRef}
            lastSelectionEventTimeRef={lastSelectionEventTimeRef}
            lastFocusEventTimeRef={lastFocusEventTimeRef}
            CHANGE_THROTTLE_MS={CHANGE_THROTTLE_MS}
            CURSOR_THROTTLE_MS={CURSOR_THROTTLE_MS}
            SELECTION_THROTTLE_MS={SELECTION_THROTTLE_MS}
            FOCUS_THROTTLE_MS={FOCUS_THROTTLE_MS}
            typingAnalyzer={typingAnalyzer}
            componentContainerRef={componentContainerRef}
            monacoLanguage={monacoLanguage}
            onLanguageChange={handleLanguageChange}
            // Output Panel Props
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            timerRef={timerTextRef}
            testCases={testCases}
            customInput={customInput}
            setCustomInput={setCustomInput}
            customResult={customResult}
            customLoading={customLoading}
            onRunCustomInput={runWithCustomInput}
            results={results}
            summary={summary}
            showCelebration={showCelebration}
            setShowCelebration={setShowCelebration}
            onFloatingToggle={handleFloatingToggle}
            vtLoading={vtLoading}
            outputHeight={outputHeight}
            questionId={questionId}
            isFirstProgrammingQuestion={
              data?.isFirstProgrammingQuestion || false
            }
            onModalStateChange={onModalStateChange}
            isAnswered={initialIsAnswered || selectedAnswerData?.isAnswered}
          />

          {/* ===== PHASE 3: ENHANCED MODAL COMPONENT ===== */}
          <CommonModal
            open={modal.isOpen}
            handleClose={closeModal}
            title={modal.title}
            type={modal.type}
            showFooter={modal.showFooter}
            isLoading={modal.isLoading}
            icon={modal.icon}
          >
            <Typography
              sx={{
                color:
                  modal.type === "error"
                    ? "#d32f2f"
                    : modal.type === "success"
                      ? "#2e7d32"
                      : modal.type === "warning"
                        ? "#ed6c02"
                        : "inherit",
                fontSize: "14px",
                lineHeight: 1.5,
              }}
            >
              {modal.message}
            </Typography>
          </CommonModal>

          {/* ✅ REMOVED: Time Warning Toast Overlay */}
          {/* Programming questions no longer have per-question time limits */}

          {/* ✅ NEW: Retry Status Component */}
          <TextAnswerRetryStatus
            questionId={data?._id}
            questionType="programming"
            onRetrySuccess={() => {
              console.log("Programming answer retry successful");
            }}
            onRetryError={(error) => {
              console.error("Programming answer retry failed:", error);
            }}
          />
        </Box>
      );
    }

    // Desktop layout
    return (
      <Box ref={componentContainerRef} sx={containerStyles}>
        {/* Question Panel */}
        <QuestionPanel
          questionTitle={questionTitle}
          questionContent={questionContent}
          sampleInput={sampleInput}
          sampleOutput={sampleOutput}
          timeLimit={timeLimit}
          memoryLimit={memoryLimit}
          maxCodeSize={maxCodeSize}
          supportedLanguages={data.supportedLanguages}
          questionContainerStyles={questionContainerStyles}
          isMobile={isMobile}
          questionIndex={questionIndex}
        />

        {/* Panel Resizer */}
        <PanelResizer
          onDragStart={startDrag}
          isDragging={isDragging}
          isMobile={isMobile}
        />

        {/* Post-Submission View or Editor Section */}
        {isPostSubmission ? (
          <Box
            sx={{
              flexGrow: 1,
              width: isMobile ? "100%" : `calc(100% - ${leftWidth}px)`,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <ProgrammingPostSubmissionView
              code={selectedAnswerData?.selectedAnswer || code}
              language={getMonacoLanguage(
                selectedAnswerData?.language || selectedLanguage,
              )}
              languageName={
                supportedLanguages.find(
                  (l) =>
                    l.languageId ===
                    (selectedAnswerData?.language || selectedLanguage),
                )?.languageName || selectedLanguage
              }
              timeSpent={selectedAnswerData?.timeSpent || timeSpentOnQuestion}
              retakes={selectedAnswerData?.retakes || attemptsUsed}
              maxAttempts={maxAttempts}
              isMobile={isMobile}
            />
          </Box>
        ) : (
          <>
            <EditorSection
              code={code}
              setCode={memoizedSetCode}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              editorTheme={editorTheme}
              setEditorTheme={setEditorTheme}
              isRunningCode={isRunningCode}
              isSubmitting={isSubmittingState}
              attemptsExhausted={attemptsExhausted}
              attemptsUsed={attemptsUsed}
              maxAttempts={maxAttempts}
              supportedLanguages={supportedLanguages}
              onRunCode={handleRunCode}
              onSubmit={handleSubmit}
              onFormatCode={handleFormatCode}
              saveState={saveState}
              lastSavedAt={lastSavedAt}
              isReadOnly={isHookDisabled}
              isAnswered={initialIsAnswered || selectedAnswerData?.isAnswered}
              timeRemaining={timeRemaining}
              showOutput={showOutput}
              setShowOutput={setShowOutput}
              isOutputFloating={isOutputFloating}
              editorRef={editorRef}
              monacoRef={monacoRef}
              trackEditorEvent={trackEditorEvent}
              lastChangeEventTimeRef={lastChangeEventTimeRef}
              lastCursorEventTimeRef={lastCursorEventTimeRef}
              lastSelectionEventTimeRef={lastSelectionEventTimeRef}
              lastFocusEventTimeRef={lastFocusEventTimeRef}
              CHANGE_THROTTLE_MS={CHANGE_THROTTLE_MS}
              CURSOR_THROTTLE_MS={CURSOR_THROTTLE_MS}
              SELECTION_THROTTLE_MS={SELECTION_THROTTLE_MS}
              FOCUS_THROTTLE_MS={FOCUS_THROTTLE_MS}
              typingAnalyzer={typingAnalyzer}
              componentContainerRef={componentContainerRef}
              monacoLanguage={monacoLanguage}
              onLanguageChange={handleLanguageChange}
              // Output Panel Props
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              testCases={testCases}
              customInput={customInput}
              setCustomInput={setCustomInput}
              customResult={customResult}
              customLoading={customLoading}
              onRunCustomInput={runWithCustomInput}
              results={results}
              summary={summary}
              showCelebration={showCelebration}
              setShowCelebration={setShowCelebration}
              onFloatingToggle={handleFloatingToggle}
              vtLoading={vtLoading}
              outputHeight={outputHeight}
              questionId={questionId}
              isFirstProgrammingQuestion={
                data?.isFirstProgrammingQuestion || false
              }
              onModalStateChange={onModalStateChange}
            />

            {/* Floating Output Window - Only on desktop */}
            {!isMobile && (
              <FloatingOutputWindow
                isFloating={isOutputFloating}
                position={floatingPosition}
                size={floatingSize}
                onDragStart={handleWindowDragStart}
                onResizeStart={handleResizeStart}
                onClose={handleFloatingToggle}
                showOutput={showOutput}
                setShowOutput={setShowOutput}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                testCases={testCases}
                customInput={customInput}
                setCustomInput={setCustomInput}
                customResult={customResult}
                customLoading={customLoading}
                onRunCustomInput={runWithCustomInput}
                results={results}
                summary={summary}
                showCelebration={showCelebration}
                setShowCelebration={setShowCelebration}
                onFloatingToggle={handleFloatingToggle}
                vtLoading={vtLoading}
              />
            )}
          </>
        )}

        {/* ===== PHASE 3: ENHANCED MODAL COMPONENT ===== */}
        <CommonModal
          open={modal.isOpen}
          handleClose={closeModal}
          title={modal.title}
          type={modal.type}
          showFooter={modal.showFooter}
          isLoading={modal.isLoading}
          icon={modal.icon}
        >
          <Typography
            sx={{
              color:
                modal.type === "error"
                  ? "#d32f2f"
                  : modal.type === "success"
                    ? "#2e7d32"
                    : modal.type === "warning"
                      ? "#ed6c02"
                      : "inherit",
              fontSize: "14px",
              lineHeight: 1.5,
            }}
          >
            {modal.message}
          </Typography>
        </CommonModal>

        {/* ✅ REMOVED: Time Warning Toast Overlay and Warning Modal */}
        {/* Programming questions no longer have per-question time limits */}

        {/* ✅ NEW: Retry Status Component */}
        <TextAnswerRetryStatus
          questionId={data?._id}
          questionType="programming"
          onRetrySuccess={() => {
            console.log("Programming answer retry successful");
          }}
          onRetryError={(error) => {
            console.error("Programming answer retry failed:", error);
          }}
        />
      </Box>
    );
  },
);

export default ProgrammingTypeQuestion;
